import React, { useRef, useState } from 'react';
import Webcam from 'react-webcam';
import CountdownTimer from '../shared/CountdownTimer';
import { captureWebcamImage } from '../../utils/imageUtils';

const CameraScreen = ({ onPhotoCapture, onBack, orientation = 'portrait' }) => {
  const webcamRef = useRef(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const [webcamError, setWebcamError] = useState(false);

  const handleStartCapture = () => {
    setShowCountdown(true);
  };

  const handleCountdownComplete = async () => {
    const imageSrc = await captureWebcamImage(webcamRef);

    if (imageSrc) {
      onPhotoCapture(imageSrc);
    } else {
      setWebcamError(true);
      setShowCountdown(false);
    }
  };

  const handleUserMedia = () => {
    setWebcamError(false);
  };

  const handleUserMediaError = (error) => {
    console.error('Webcam error:', error);
    setWebcamError(true);
  };

  // 2:3 aspect ratio for consistent overlay placement (1280x1920)
  const videoConstraints = {
    width: 1280,
    height: 1920,
    facingMode: 'user',
    aspectRatio: 2 / 3
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center relative overflow-hidden">
      {/* Webcam Feed - Fixed aspect ratio container */}
      <div className="relative flex items-center justify-center w-full h-screen">
        {!webcamError ? (
          <>
            {/* Fixed aspect ratio container for 2:3 */}
            <div className="relative" style={{ aspectRatio: '2/3', height: '100vh', maxWidth: '100vw' }}>
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                onUserMedia={handleUserMedia}
                onUserMediaError={handleUserMediaError}
                className="w-full h-full object-cover"
                mirrored={true}
                style={{ aspectRatio: '2/3' }}
              />

              {/* Camera Frame Overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-8 border-4 border-white/50 rounded-3xl"></div>

                {/* Corner decorations */}
                <div className="absolute top-8 left-8 w-16 h-16 border-t-4 border-l-4 border-[#e5181e]"></div>
                <div className="absolute top-8 right-8 w-16 h-16 border-t-4 border-r-4 border-[#e5181e]"></div>
                <div className="absolute bottom-8 left-8 w-16 h-16 border-b-4 border-l-4 border-[#e5181e]"></div>
                <div className="absolute bottom-8 right-8 w-16 h-16 border-b-4 border-r-4 border-[#e5181e]"></div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center p-8">
            <div className="text-6xl mb-4">📷</div>
            <h2 className="text-2xl font-bold text-white mb-4">Camera Not Available</h2>
            <p className="text-gray-400 mb-8">
              Please allow camera access to continue or check if your camera is working properly.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-8 py-4 bg-[#e5181e] text-white rounded-full font-semibold hover:bg-[#c01419] active:scale-95 transition"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Instructions */}
      {!webcamError && !showCountdown && (
        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm px-8 py-4 rounded-full z-10">
          <p className="text-white text-2xl font-semibold">
            Position yourself in the frame
          </p>
        </div>
      )}

      {/* Capture Button - Right Side Center */}
      {!webcamError && !showCountdown && (
        <button
          onClick={handleStartCapture}
          className="absolute right-8 top-1/2 transform -translate-y-1/2 w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-full border-6 sm:border-8 border-jeep-red hover:bg-red-100 transition hover:scale-105 active:scale-95 shadow-2xl z-10"
          aria-label="Take photo"
        >
          <div className="w-full h-full rounded-full bg-jeep-red"></div>
        </button>
      )}

      {/* Back Button - Below Shutter Button */}
      {!webcamError && !showCountdown && (
        <button
          onClick={onBack}
          className="absolute right-8 top-1/2 transform translate-y-16 sm:translate-y-20 px-6 sm:px-8 py-3 sm:py-4 bg-gray-700/90 backdrop-blur-sm text-white rounded-full font-semibold hover:bg-gray-600 active:scale-95 transition text-lg sm:text-xl shadow-2xl z-10"
        >
          ← Back
        </button>
      )}

      {/* Countdown Overlay */}
      {showCountdown && <CountdownTimer onComplete={handleCountdownComplete} />}
    </div>
  );
};

export default CameraScreen;
